import IRequirementCreateService from 'features/requirements/services/IRequirementCreateService';
import PageCard from 'features/shared/components/card/PageCard';
import FormGroup from 'features/shared/forms/components/FormGroup';
import { useForm } from 'features/shared/forms/hooks/useForm';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React, { useEffect, useState } from 'react';
import { distRateCircuitBreakerFormValidations } from './distRateCircuitBreakerFormValidation';
import ContractDropdownDTO from 'features/contracts/dtos/ContractDropdownDTO';
import ContractSelect from 'features/contracts/components/ContractSelect';
import { Nullable } from 'features/shared/types/nullable';
import { trackPromise } from 'react-promise-tracker';
import BasicInfoSubFormSection from 'features/requirements/sections/requirementFormTypes/basicInfoSubForm/BasicInfoSubFormSection';
import IDistRateCircuitBreakerFormService from 'features/requirements/services/IDistRateCircuitBreakerFormService';
import DatePicker from 'features/shared/components/reactDates/DatePicker';
import moment from 'moment';
import DistRateCircuitBreakerSubForm from 'features/requirements/components/distRateCircuitBreakerForm/DistRateCircuitBreakerSubForm';
import DistributionRateTypeEnumDTO from 'features/shared/enums/dynamic/DistributionRateTypeEnumDTO';
import IRequirementFormService from 'features/requirements/services/IRequirementFormService';
import formatISO from 'date-fns/formatISO';
import { DistRateCircuitBreakerFormSection_ContractCard } from 'features/shared/components/loaders/promiseLoadingAreas';
import { useTranslation } from 'react-i18next';
import { openRequirementResultDialog } from 'features/requirements/helpers/requirementResultDialogHelper';

interface Props {
  contracts: ContractDropdownDTO[];
}

const DistRateCircuitBreakerFormSection = ({ contracts }: Props) => {
  //#region translations
  const tLabels = useTranslation("requirements\\requirements").t;
  //#endregion translations

  //#region DI
  const createService = useInjection<IRequirementCreateService>(IocTypes.RequirementCreateService);
  const formService = useInjection<IDistRateCircuitBreakerFormService>(IocTypes.DistRateCircuitBreakerFormService);
  const baseFormService = useInjection<IRequirementFormService>(IocTypes.RequirementFormService);
  //#endregion DI

  //#region state
  const [selectedContract, setSelectedContract] = useState<ContractDropdownDTO>({});
  const [selectedDate, setSelectedDate] = useState<Nullable<Date>>(null);
  const [maxDate, setMaxDate] = useState<Nullable<Date>>(null);
  const [distributionRates, setDistributionRates] = useState<DistributionRateTypeEnumDTO[]>([]);
  //#endregion state

  //#region form hook
  const { values, setFormValue, errors, bindField, bindUncontrolledField, valideteForm, validateFormField, setValidationRule } = useForm({
    initialValidations: distRateCircuitBreakerFormValidations()
  });
  //#endregion

  //#region side-effects
  useEffect(() => {
    // set default contract
    if (contracts.length === 1) {
      contractChangeHandler(contracts[0]);
    }
  }, [contracts]);
  //#endregion

  //#region helpers
  const fetchData = async (contract: ContractDropdownDTO) => {
    let contractEnergy = await formService.fetchElectricityLowVoltage(contract.contractId!);
    let distributionRates = await formService.fetchDistributionRates(contract.contractId!);

    return {
      contractEnergy: contractEnergy,
      distributionRates: distributionRates
    }
  }
  //#endregion helpers

  //#region handlers
  const submitFormHandler = async (e: React.FormEvent) => {
    e.preventDefault();
    if (valideteForm()) {
      let valuesCopy = {...values};
      valuesCopy["DESC_TMZMSMLEEJISTIC"] = `${values["DESC_TMZMSMLEEJISTIC"]}`.replace(",", ".");

      let result = await createService.createRequirement("ZMENADISTRIBUCNISAZBY_JISTICE_REZERVOVANEHOPRIKONU", valuesCopy);
      openRequirementResultDialog(false, { response: result });
    }
  };

  const contractChangeHandler = async (contract: ContractDropdownDTO | null) => {
    if (contract) {
      // fetch data
      const { contractEnergy, distributionRates } = await trackPromise(fetchData(contract), DistRateCircuitBreakerFormSection_ContractCard);

      // dates
      let now = new Date();
      let nextMonthFirstDay = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      let maxDate: Nullable<Date> = contract.contractDurationType === "CertainTime" ? new Date(contract.terminationDate!) : null;

      // set state
      setSelectedContract(contract);
      setDistributionRates(distributionRates);
      setSelectedDate(nextMonthFirstDay);
      setMaxDate(maxDate);

      // set select data to form values
      setFormValue("DESC_TMZMSMLEESMLOUVA", contract.contractId);
      setFormValue("DESC_TMZMSMLEEDATUMPOZZME", formatISO(nextMonthFirstDay, { representation: "date"} ));
      formService.setFetchedContractEnergy(contractEnergy, setFormValue);

      // after setting values re-validate form
      validateFormField("DESC_TMZMSMLEESMLOUVA", contract.contractId);
      validateFormField("DESC_TMZMSMLEEDATUMPOZZME", nextMonthFirstDay);
      validateFormField("DESC_TMZMSMLEEJISTIC", String(contractEnergy.circuitBreaker));
    }
  }

  const dateChangeHandler = (date: Nullable<moment.Moment>) => {
    if (date) {
      setSelectedDate(date.toDate());
      setFormValue("DESC_TMZMSMLEEDATUMPOZZME", date.format(moment.HTML5_FMT.DATE));   
    }
  }
  //#endregion handlers

  //#region render
  return (
    <form onSubmit={submitFormHandler}>
      <PageCard 
        title={tLabels("distRateCircuitBreakerFormSection.cardTitle")}
        bodyClassName="pt-4 px-4"
        loadingAreaName={DistRateCircuitBreakerFormSection_ContractCard}
      >
        <div className="position-relative">
          <div className="row no-gutters mx-n2">
            <div className="col-lg-6 px-2">
              <ContractSelect 
                name="DESC_TMZMSMLEESMLOUVA"
                contracts={contracts}
                onChange={contractChangeHandler}
                errors={errors}
                defaultContract={selectedContract}
              />
            </div>
            <div className="col-lg-6 px-2">
              <FormGroup
                labelText={tLabels("distRateCircuitBreakerFormSection.dateOfChangeRequest")}
                validationError={errors ? errors["DESC_TMZMSMLEEDATUMPOZZME"] : undefined}
              >
                <DatePicker 
                  name="DESC_TMZMSMLEEDATUMPOZZME"
                  onChange={dateChangeHandler}
                  selectedDate={selectedDate}
                  onlySelectable
                  minDate={new Date()}
                  maxDate={maxDate}
                />
              </FormGroup>
            </div>
          </div>
        </div>
      </PageCard>

      <DistRateCircuitBreakerSubForm 
        bindField={bindField}
        values={values}
        errors={errors}
        distributionRates={distributionRates}
        editable={baseFormService.isFilledValues(values, ["DESC_TMZMSMLEESMLOUVA"])}
      />

      <BasicInfoSubFormSection
        bindField={bindField}
        bindUncontrolledField={bindUncontrolledField}
        errors={errors}
        setValidationRule={setValidationRule}
      />

      <button type="submit" className="btn btn-primary px-3 py-2">{tLabels("distRateCircuitBreakerFormSection.sendRequirement")}</button>
    </form>
  )
  //#endregion render
}

export default DistRateCircuitBreakerFormSection;
